import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, RevealStagger, revealItem } from "../components/Reveal";
import { GALLERY } from "../data/site";
import { useLang } from "../context/LanguageContext";

export default function Gallery() {
  const { t } = useLang();
  const [active, setActive] = useState(null); // index or null

  const close = () => setActive(null);
  const prev = () => setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setActive((i) => (i + 1) % GALLERY.length);

  return (
    <div className="App">
      <Navbar />

      <section className="bg-brand-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-brand-sky font-heading font-bold uppercase tracking-[0.2em] text-sm">
            <Camera size={16} /> {t.nav.gallery}
          </span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-3">{t.nav.gallery}</h1>
          <p className="text-white/80 mt-3 max-w-2xl">{t.about.title} — San Juan del Sur, Nicaragua 🇳🇮</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealStagger className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" stagger={0.05}>
            {GALLERY.map((img, i) => (
              <motion.button
                key={i}
                variants={revealItem}
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${i}`}
                className="mb-5 block w-full overflow-hidden rounded-2xl bg-slate-50 border border-slate-100 group relative"
              >
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute inset-0 bg-brand-blue/0 group-hover:bg-brand-blue/10 transition-colors" />
              </motion.button>
            ))}
          </RevealStagger>
        </div>
      </section>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button onClick={close} data-testid="gallery-close" className="absolute top-5 right-5 text-white/80 hover:text-white p-2"><X size={30} /></button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 sm:left-8 text-white/80 hover:text-white p-2 bg-white/10 rounded-full"><ChevronLeft size={28} /></button>
            <motion.img
              key={active}
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              src={GALLERY[active].src} alt={GALLERY[active].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-xl"
            />
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 sm:right-8 text-white/80 hover:text-white p-2 bg-white/10 rounded-full"><ChevronRight size={28} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
